// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/** The `event_type` a pack's deadline row declares to say "this framework has a provider register". */
export const DATA_SERVICE_PROVIDER_EVENT_TYPE = 'provider_notification_lifecycle';
/** `register_surface` value pack forms declare to feed this register's vocabularies. */
export const DATA_SERVICE_PROVIDER_REGISTER_SURFACE = 'data_service_provider';
/** The form field id whose `options[]` the vocabulary reader collects. */
export const DATA_SERVICE_PROVIDER_VOCABULARY_FIELDS = {
    filingChannels: 'channel',
};
/**
 * The `sla_type` values a declaring deadline row uses so the register can
 * tell the three clocks apart without a date literal in src:
 * - `existing_provider_notification_window` — a countdown from commencement
 *   for a provider operating on that day (anchored on the framework's
 *   `effective_date`);
 * - `pre_operation_notification` — an ordering with no numeric clock;
 * - `periodic_report` — a cadence anchored on `recurrence_month` /
 *   `recurrence_day` the row carries.
 */
export const DATA_SERVICE_PROVIDER_SLA_TYPES = {
    existingProviderWindow: 'existing_provider_notification_window',
    preOperationNotification: 'pre_operation_notification',
    periodicReport: 'periodic_report',
};
/** The boundary tag this register reads. */
export const DATA_SERVICE_PROVIDER_BOUNDARY_TAG = 'data_service_provider_boundary';
// ── Computed states (read-time, never stored) ──
/**
 * Where the first notification fell against the declared window. `inside` /
 * `outside` only for a pre-existing provider with a numeric window; a new
 * provider's duty is an ordering, and reads `no_numeric_clock_declared`.
 */
export const NOTIFICATION_WINDOW_STATES = [
    'inside',
    'outside',
    'not_recorded',
    'no_numeric_clock_declared',
    'no_installed_window',
    'framework_record_unreadable',
    /** The row declares a numeric window in a unit the platform cannot compute; the pack's figure is shown, no date. */
    'unsupported_unit',
    /**
     * Sprint 62.25.0 (owner ruling OD8 (a) EXCLUDE) — an installed framework DOES
     * declare this, and the organisation installed it for reference only, so its
     * clock is not run. Never reported as "no installed framework declares one".
     */
    'declared_by_reference_install',
    /**
     * Sprint 62.25.0 — an installed framework declares this and the install-mode
     * policy excludes it for another reason (superseded, retired), so its clock
     * is not run.
     */
    'declaring_framework_not_operational',
];
/**
 * Why an `unsupported_unit` window computes no date — gate E12.5 pass 1
 * (F13). The state used to cover three causes under one name, and the export
 * sentence blamed the unit for all of them:
 * - `unit_not_computable` — a numeric figure in a unit the platform's
 *   calendar cannot compute; the pack's figure governs.
 * - `anchor_not_recorded` — the row declares a numeric figure but this record
 *   carries no date it counts from (a new provider's first day of operation
 *   is not recorded); the unit may be perfectly computable.
 * - `no_numeric_figure` — the row states no figure in a unit not declared as
 *   an ordering; a content fact about the row.
 * `null` in every other state.
 */
export const UNSUPPORTED_UNIT_REASONS = [
    'unit_not_computable',
    'anchor_not_recorded',
    'no_numeric_figure',
];
/**
 * Per reporting year. A year with no entry reads `no_report_recorded` until
 * its declared deadline has passed and `not_filed_by_deadline` after it —
 * never *overdue* before the date.
 */
export const PERIODIC_REPORT_STATES = [
    'recorded',
    'no_report_recorded',
    'not_filed_by_deadline',
    'no_installed_cadence',
    /**
     * Sprint 62.25.0 (owner ruling OD8 (a) EXCLUDE) — an installed framework DOES
     * declare this, and the organisation installed it for reference only, so its
     * clock is not run. Never reported as "no installed framework declares one".
     */
    'declared_by_reference_install',
    /**
     * Sprint 62.25.0 — an installed framework declares this and the install-mode
     * policy excludes it for another reason (superseded, retired), so its clock
     * is not run.
     */
    'declaring_framework_not_operational',
];
// ── Collections ──
export const DATA_SERVICE_PROVIDER_RECORDS_COLLECTION = 'grc_data_service_provider_records';
// ── Error codes ──
export const DATA_SERVICE_PROVIDER_ERROR_CODES = {
    VOCABULARY_VALUE_UNKNOWN: 'VOCABULARY_VALUE_UNKNOWN',
    OFFERING_NOT_FOUND: 'OFFERING_NOT_FOUND',
    AUTHORITY_REQUEST_NOT_FOUND: 'AUTHORITY_REQUEST_NOT_FOUND',
    /** One periodic report entry per reporting year. */
    REPORT_YEAR_ALREADY_RECORDED: 'REPORT_YEAR_ALREADY_RECORDED',
    /**
     * The value is deliberately not shaped like a framework code: `NO_…` reads
     * as a jurisdiction-prefixed code to `lint:framework-hardcoding`, which is
     * exactly the class of literal R-46 forbids in `src/`.
     */
    NO_DECLARING_FRAMEWORK: 'DECLARING_FRAMEWORK_NOT_INSTALLED',
};
//# sourceMappingURL=data-services.js.map